import React, { useEffect, useState } from "react";
import { Badge, Button, Card, Table } from "react-bootstrap";

export default function LeadWorkTimelineCard({ rows, breaks = [], gaps = null, onOpenLead, title = "Selected Employee Work Sessions", emptyText = "No lead work sessions in the selected period." }) {
  const [, setClockTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setClockTick((value) => value + 1), 30000);
    return () => clearInterval(timer);
  }, []);
  const activityTime = (row) => Date.parse(row.active ? new Date().toISOString() : (row.endedAt || row.startedAt || "")) || 0;
  const sorted = [...(rows || [])].sort((a, b) => activityTime(b) - activityTime(a));
  // When the caller supplies gaps (the backend's authoritative idle windows, from the same
  // per-session calculation that drives gapMinutes elsewhere), use those directly instead of
  // guessing gaps from rows - which is grouped per-lead (multiple sessions on the same lead
  // collapsed into one row) and can miss or misplace idle time as a result.
  const tableRows = selectedLeadWorkRows(rows, breaks, gaps);
  let leadRowNumber = 0;
  const timelineRows = hourlyTimelineRows(tableRows);
  const sessionSummary = selectedSessionSummary(sorted);
  const breakSummary = selectedBreakSummary(breaks);
  const displayIdleSeconds = tableRows.filter((item) => item.type === "gap").reduce((total, item) => total + item.seconds, 0);
  return <Card className="mt-3 border-primary-subtle"><Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2"><span className="fw-semibold">{title}</span><div className="d-flex flex-wrap gap-2 small"><Badge bg="primary">Unique Leads {sessionSummary.uniqueLeads}</Badge><Badge bg="secondary">Sessions {sessionSummary.sessions}</Badge><Badge bg="success">Lead Time {formatSeconds(sessionSummary.totalSeconds)}</Badge>{breakSummary.breakCount > 0 && <Badge bg="info" text="dark">Breaks {breakSummary.breakCount} | {formatSeconds(breakSummary.breakSeconds)}</Badge>}{breakSummary.lunchCount > 0 && <Badge bg="success">Lunch {breakSummary.lunchCount} | {formatSeconds(breakSummary.lunchSeconds)}</Badge>}{displayIdleSeconds > 0 && <Badge bg="warning" text="dark">Idle Time {formatSeconds(displayIdleSeconds)}</Badge>}</div></Card.Header><div className="d-flex align-items-stretch">{timelineRows.length > 0 && <HourWiseRail rows={timelineRows} />}<div className="table-responsive flex-grow-1"><Table hover size="sm" className="mb-0 align-middle"><thead><tr><th style={{ width: 44 }}>#</th><th>Lead</th><th>Customer</th><th>Destination</th><th>Interest From to To</th><th>Follow-up Status</th><th>Call Data</th><th>Started</th><th>Ended</th><th className="text-end">Sessions</th><th className="text-end">Time Spent</th><th className="text-end">Actual Call Time</th><th>Status</th></tr></thead><tbody>{tableRows.map((item, index) => item.type === "lead" ? <LeadWorkTableRow key={`${item.row.leadId || item.row.leadCode || "lead"}-${item.row.memberId || item.row.username || "member"}-${item.row.startedAt || index}`} rowNumber={++leadRowNumber} row={item.row} onOpenLead={onOpenLead} /> : <LeadWorkEventRow key={`${item.type}-${index}-${item.startedAt}`} item={item} />)}{!sorted.length && <tr><td colSpan={13} className="text-muted text-center py-3">{emptyText}</td></tr>}</tbody></Table></div></div></Card>;
}

function LeadWorkTableRow({ rowNumber, row, onOpenLead }) {
  return <tr><td className="small fw-semibold">{rowNumber}</td><td className="fw-semibold"><Button type="button" variant="link" size="sm" className="p-0 fw-semibold align-baseline" onClick={() => onOpenLead?.(row)}>{row.leadCode || `#${row.leadId}`}</Button></td><td>{row.customerNumber || "-"}</td><td><div>{row.destinationName || "-"}</div>{row.categoryName && <div className="small text-muted">{row.categoryName}</div>}</td><td>{formatStatusChange(row.interestStatusFrom, row.interestStatusTo)}</td><td>{row.followUpStatus || "-"}</td><td><CallDataCell row={row} /></td><td>{formatDateTime(row.startedAt)}</td><td>{row.active ? "-" : formatDateTime(row.endedAt)}</td><td className="text-end">{row.sessionCount || 0}</td><td className="text-end fw-semibold">{formatSeconds(row.totalSeconds || 0)}</td><td className="text-end text-nowrap fw-semibold">{formatSeconds(row.actualCallSeconds || 0)}</td><td><Badge bg={row.active ? "primary" : "secondary"}>{row.active ? "Working Now" : "Worked Today"}</Badge></td></tr>;
}
function CallDataCell({ row }) {
  const sChange = Number(row.successfulCallsChanged || 0);
  const uChange = Number(row.unansweredCallsChanged || 0);
  const changes = [];
  if (sChange !== 0) changes.push(<Badge key="s" bg={sChange > 0 ? "success" : "secondary"}>S {formatSignedNumber(sChange)}</Badge>);
  if (uChange !== 0) changes.push(<Badge key="u" bg={uChange > 0 ? "warning" : "secondary"} text={uChange > 0 ? "dark" : undefined}>U {formatSignedNumber(uChange)}</Badge>);
  return <div className="d-flex flex-wrap gap-1"><Badge bg="light" text="dark">Total S {row.successfulCalls || 0} / U {row.unansweredCalls || 0}</Badge>{changes.length > 0 ? changes : <Badge bg="secondary">No change</Badge>}</div>;
}
function LeadWorkEventRow({ item }) {
  const variant = item.type === "lunch" ? "success" : item.type === "break" ? "info" : "warning";
  const darkText = item.type !== "lunch";
  return <tr className="table-light"><td colSpan={13} className="py-1"><div className="d-flex flex-wrap align-items-center gap-2 small"><Badge bg={variant} text={darkText ? "dark" : undefined}>{item.label}</Badge><span className="fw-semibold">{formatSeconds(item.seconds)}</span><span className="text-muted">{formatDateTime(item.startedAt)} - {formatDateTime(item.endedAt)}</span>{item.subtitle && <span className="text-muted">| {item.subtitle}</span>}</div></td></tr>;
}
function HourWiseRail({ rows }) {
  return <div className="border-end bg-light-subtle px-1 py-2" style={{ width: 58, flex: "0 0 58px" }} title="Hour wise timeline"><div className="position-relative h-100" style={{ minHeight: Math.max(80, rows.length * 30) }}><div style={{ position: "absolute", left: 35, top: 8, bottom: 8, width: 2, background: "#cbd5e1" }} />{rows.map((row) => { const leadCount = hourLeadItemCount(row); return <div key={row.hourKey} className="position-relative d-flex align-items-center" style={{ minHeight: 30 }} title={hourTimelineTitle(row)}><span className="text-muted text-end small" style={{ width: 28, fontSize: 10, lineHeight: 1 }}>{row.shortLabel}</span><span className="ms-1 rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: 16, height: 16, zIndex: 1, fontSize: 9, fontWeight: 700, color: leadCount ? "#fff" : "#64748b", background: leadCount ? "#0d6efd" : "#e2e8f0", border: "1px solid #94a3b8" }}>{leadCount || ""}</span></div>; })}</div></div>;
}

function pad2(value) { return String(value).padStart(2, "0"); }
function formatDateOnly(value) {
  if (!value) return "-";
  const text = String(value);
  const dateOnly = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnly) return `${dateOnly[3]}/${dateOnly[2]}/${dateOnly[1]}`;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return text;
  return `${pad2(date.getDate())}/${pad2(date.getMonth() + 1)}/${date.getFullYear()}`;
}
function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return formatValue(value);
  const hours = date.getHours();
  const period = hours >= 12 ? "PM" : "AM";
  return `${formatDateOnly(value)} ${pad2(hours % 12 || 12)}:${pad2(date.getMinutes())} ${period}`;
}
function formatValue(value) {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) return formatDateOnly(value);
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) return formatDateTime(value);
  return String(value);
}
function formatStatusChange(from, to) {
  const fromText = formatValue(from);
  const toText = formatValue(to);
  if (fromText === "-" && toText === "-") return "-";
  if (fromText === "-" || fromText === toText) return toText;
  if (toText === "-") return fromText;
  return <span className="small">{fromText} <span className="text-muted">to</span> {toText}</span>;
}
function formatSignedNumber(value) {
  const number = Number(value || 0);
  return `${number > 0 ? "+" : ""}${number}`;
}
function selectedSessionSummary(rows = []) {
  const uniqueLeadKeys = new Set();
  let sessions = 0;
  let totalSeconds = 0;
  (rows || []).forEach((row) => {
    const leadKey = row.leadId || row.leadCode;
    if (leadKey) uniqueLeadKeys.add(String(leadKey));
    sessions += Math.max(1, Number(row.sessionCount || 1));
    totalSeconds += Number(row.totalSeconds || 0);
  });
  return { uniqueLeads: uniqueLeadKeys.size, sessions, totalSeconds };
}
function formatSeconds(totalSeconds) {
  const seconds = Math.max(0, Number(totalSeconds || 0));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${remainingSeconds}s`;
  return `${remainingSeconds}s`;
}
function timestamp(value) {
  const time = Date.parse(value || "");
  return Number.isNaN(time) ? 0 : time;
}
function isLunchBreak(startMs, endMs, seconds) {
  if (seconds < 20 * 60 || !startMs || !endMs) return false;
  const start = new Date(startMs);
  const lunchStart = new Date(start);
  lunchStart.setHours(12, 0, 0, 0);
  const lunchEnd = new Date(start);
  lunchEnd.setHours(15, 30, 0, 0);
  return startMs < lunchEnd.getTime() && endMs > lunchStart.getTime();
}
function selectedLeadWorkRows(rows = [], breaks = [], gaps = null) {
  const now = Date.now();
  const leadRows = [...(rows || [])]
    .map((row) => {
      const startMs = timestamp(row.startedAt);
      const endMs = row.active || !row.endedAt ? now : timestamp(row.endedAt);
      return { type: "lead", row, startMs, endMs: Math.max(startMs, endMs) };
    })
    .filter((item) => item.startMs > 0);
  const breakRows = (breaks || []).map((row) => {
    const startMs = timestamp(row.startedAt);
    const endMs = row.active || !row.endedAt ? now : timestamp(row.endedAt);
    const normalizedEndMs = Math.max(startMs, endMs);
    const seconds = Math.max(0, Math.floor((normalizedEndMs - startMs) / 1000));
    const lunch = isLunchBreak(startMs, normalizedEndMs, seconds);
    return {
      type: lunch ? "lunch" : "break",
      label: lunch ? "Lunch" : "Break",
      row,
      startMs,
      endMs: normalizedEndMs,
      seconds,
      startedAt: new Date(startMs).toISOString(),
      endedAt: new Date(normalizedEndMs).toISOString(),
      subtitle: row.active ? "Active now" : "",
    };
  }).filter((item) => item.startMs > 0);

  if (Array.isArray(gaps)) {
    const gapRows = gaps.map((gap) => {
      const startMs = timestamp(gap.startedAt);
      const endMs = Math.max(startMs, timestamp(gap.endedAt));
      return {
        type: "gap",
        label: "Idle Time",
        startMs,
        endMs,
        seconds: Math.max(0, Math.floor((endMs - startMs) / 1000)),
        startedAt: new Date(startMs).toISOString(),
        endedAt: new Date(endMs).toISOString(),
        subtitle: "No lead work or break recorded",
      };
    }).filter((item) => item.startMs > 0);
    return [...leadRows, ...breakRows, ...gapRows]
      .sort((a, b) => b.startMs - a.startMs)
      .map((current) => current.type === "lead" ? { type: "lead", row: current.row } : {
        type: current.type,
        label: current.label,
        seconds: current.seconds,
        startedAt: current.startedAt,
        endedAt: current.endedAt,
        subtitle: current.subtitle,
      });
  }

  // No authoritative gaps supplied - fall back to inferring them from spacing between the rows we
  // do have (kept for any caller that hasn't been wired up to pass real gaps yet).
  const displayRows = [...leadRows, ...breakRows].sort((a, b) => b.startMs - a.startMs);
  const result = [];
  for (let index = 0; index < displayRows.length; index += 1) {
    const current = displayRows[index];
    const previous = displayRows[index + 1];
    result.push(current.type === "lead" ? { type: "lead", row: current.row } : {
      type: current.type,
      label: current.label,
      seconds: current.seconds,
      startedAt: current.startedAt,
      endedAt: current.endedAt,
      subtitle: current.subtitle,
    });
    if (!previous) continue;
    const seconds = Math.floor((current.startMs - previous.endMs) / 1000);
    if (seconds < 60) continue;
    result.push({
      type: "gap",
      label: "Idle Time",
      seconds,
      startedAt: new Date(previous.endMs).toISOString(),
      endedAt: new Date(current.startMs).toISOString(),
      subtitle: "No lead work or break recorded",
    });
  }
  return result;
}
function selectedBreakSummary(breaks = []) {
  return (breaks || []).reduce((summary, row) => {
    const startMs = timestamp(row.startedAt);
    const endMs = row.active || !row.endedAt ? Date.now() : timestamp(row.endedAt);
    const seconds = Math.max(0, Math.floor((Math.max(startMs, endMs) - startMs) / 1000));
    const lunch = isLunchBreak(startMs, Math.max(startMs, endMs), seconds);
    if (lunch) {
      summary.lunchCount += 1;
      summary.lunchSeconds += seconds;
    } else {
      summary.breakCount += 1;
      summary.breakSeconds += seconds;
    }
    return summary;
  }, { breakCount: 0, breakSeconds: 0, lunchCount: 0, lunchSeconds: 0, gapSeconds: 0 });
}
function hourlyTimelineRows(items = []) {
  const now = Date.now();
  const rows = new Map();
  let minHourMs = null;
  let maxHourMs = null;
  [...items].sort((a, b) => timelineStart(a, now) - timelineStart(b, now)).forEach((item) => {
    const startMs = timelineStart(item, now);
    const endMs = Math.max(startMs, timelineEnd(item, now));
    if (!startMs) return;
    const start = new Date(startMs);
    const hourStart = new Date(start);
    hourStart.setMinutes(0, 0, 0);
    const hourEnd = new Date(endMs);
    hourEnd.setMinutes(0, 0, 0);
    minHourMs = minHourMs == null ? hourStart.getTime() : Math.min(minHourMs, hourStart.getTime());
    maxHourMs = maxHourMs == null ? hourEnd.getTime() : Math.max(maxHourMs, hourEnd.getTime());
    const hourKey = hourStart.toISOString();
    const existing = rows.get(hourKey) || timelineHourRow(hourStart);
    existing.items.push({
      type: item.type,
      label: timelineItemLabel(item),
      leadId: item.type === "lead" ? item.row?.leadId : null,
      seconds: Math.max(0, Math.floor((endMs - startMs) / 1000)),
    });
    rows.set(hourKey, existing);
  });
  if (minHourMs == null || maxHourMs == null) return [];
  const result = [];
  for (let hourMs = minHourMs; hourMs <= maxHourMs; hourMs += 60 * 60 * 1000) {
    const date = new Date(hourMs);
    result.push(rows.get(date.toISOString()) || timelineHourRow(date));
  }
  return result;
}
function timelineHourRow(date) {
  return { hourKey: date.toISOString(), label: hourLabel(date), shortLabel: hourShortLabel(date), items: [] };
}
function hourTimelineTitle(row) {
  if (!row.items.length) return `${row.label}: No tracked activity`;
  return `${row.label}: ${row.items.map((item) => `${item.label} - ${formatSeconds(item.seconds)}`).join(", ")}`;
}
function hourLeadItemCount(row) {
  const leadKeys = new Set();
  (row.items || []).forEach((item) => {
    if (item.type !== "lead") return;
    const key = item.leadId || item.label;
    if (key) leadKeys.add(key);
  });
  return leadKeys.size;
}
function timelineStart(item, now) {
  if (!item) return 0;
  if (item.type === "lead") return timestamp(item.row?.startedAt);
  return timestamp(item.startedAt);
}
function timelineEnd(item, now) {
  if (!item) return 0;
  if (item.type === "lead") return item.row?.active || !item.row?.endedAt ? now : timestamp(item.row.endedAt);
  return timestamp(item.endedAt) || now;
}
function timelineItemLabel(item) {
  if (item.type === "lead") return item.row?.leadCode || `#${item.row?.leadId || ""}`;
  return item.label || "Idle Time";
}
function hourLabel(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  const hours = date.getHours();
  return `${pad2(hours % 12 || 12)} ${hours >= 12 ? "PM" : "AM"}`;
}
function hourShortLabel(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  const hours = date.getHours();
  return `${hours % 12 || 12}${hours >= 12 ? "p" : "a"}`;
}
